import { reactive, inject } from 'vue'
import type { InjectionKey } from 'vue'
import { schemaValidate } from '@/utils/yup'
import type { SimpleHealthUnitResponse } from '@/Network/Types/Responses/HealthUnitResponses'
import {
  HealthUnitSchema,
  createEmptyHealthUnitValues,
  createEmptyHealthUnitErrors
} from '../schemas'
import type { HealthUnit, ErrorUnit } from '../schemas'
import { maskPhoneInput } from '../helpers/phone'
import { maskCnpjInput } from '../helpers/cnpj'
import { maskPostalCodeInput } from '../helpers/postalCode'

export type HealthUnitFormState = {
  values: HealthUnit
  errors: ErrorUnit
}

/**
 * Estado do formulário de unidade — uma instância NOVA por chamada (mata o
 * antigo singleton compartilhado). O orquestrador cria via useHealthUnitForm()
 * e fornece aos componentes de campos com provide(HealthUnitFormKey, ...).
 */
function populateDirectFields(values: HealthUnit, unit: SimpleHealthUnitResponse): void {
  values.name = unit.name ?? ''
  values.type = unit.type ?? undefined
  values.cnpj = maskCnpjInput(unit.cnpj)
  values.email = unit.email ?? ''
  values.mainPhone = maskPhoneInput(unit.mainPhone?.phoneNumber)
  values.technicalManager = unit.technicalManager ?? ''
  values.generalManager = unit.generalManager ?? ''
  values.quantityColaborators = unit.quantityColaborators ?? undefined
  values.profile = unit.profile ?? ''
  values.avatarUrl = unit.avatarUrl ?? ''
}

function populateAddressFields(
  values: HealthUnit,
  address: SimpleHealthUnitResponse['mainAddress']
): void {
  values.postalCode = maskPostalCodeInput(address?.postalCode)
  values.city = address?.city ?? ''
  values.complement = address?.complement ?? ''
  values.neighborhood = address?.neighborhood ?? ''
  values.number = address?.number ?? ''
  values.state = address?.state ?? ''
  values.street = address?.street ?? ''
}

export function useHealthUnitForm() {
  const form = reactive<HealthUnitFormState>({
    values: createEmptyHealthUnitValues(),
    errors: createEmptyHealthUnitErrors()
  })

  const validate = async (field: keyof HealthUnit) => {
    await schemaValidate(field, form, HealthUnitSchema)
  }

  const rules = {
    form: (field: keyof ErrorUnit) => !form.errors[field] || form.errors[field],
    required: (v: unknown) => !!v || 'Campo obrigatório'
  }

  /** Preenche os campos do form a partir da unidade em edição. */
  function populateFromUnit(unit: SimpleHealthUnitResponse): void {
    populateDirectFields(form.values, unit)
    populateAddressFields(form.values, unit.mainAddress)
    form.values.linkWaze = unit.linkWaze ?? ''
  }

  /**
   * Limpa o formulário. Semântica idêntica ao clear() histórico: valores
   * viram undefined (não os defaults), erros viram string vazia.
   */
  function resetForm(): void {
    ;(Object.keys(form.values) as Array<keyof HealthUnit>).forEach((key) => {
      form.values[key] = undefined as never
    })
    ;(Object.keys(form.errors) as Array<keyof ErrorUnit>).forEach((key) => {
      form.errors[key] = ''
    })
  }

  return { form, validate, rules, populateFromUnit, resetForm }
}

export type HealthUnitFormApi = ReturnType<typeof useHealthUnitForm>

export const HealthUnitFormKey: InjectionKey<HealthUnitFormApi> = Symbol('health-unit-form')

/** Inject com guarda — os componentes de campos exigem o form do orquestrador. */
export function injectHealthUnitForm(): HealthUnitFormApi {
  const api = inject(HealthUnitFormKey)
  if (!api) {
    throw new Error('injectHealthUnitForm: uso fora de um provider de HealthUnitFormKey')
  }
  return api
}
